import * as React from "react";
import { useHistory } from "react-router";
import authService from "utils/AuthService";
import { StorageKeys } from "utils/Authentication";
import { Redirect } from "react-router";
import Login from "components/authentication/Login";
import VerifyCode from "components/authentication/VerifyCode";
import {
  Auth0Errors,
  AuthServiceErrors,
  ErrorCode,
  LoginScopeEnum,
  LoginVerificationType,
  VerifyCodeFeedback,
} from "models/Constants";
import { getSessionMetadata, maskEmail } from "utils/Helpers";
import LoginVerificationOptions from "components/authentication/LoginVerificationOptions";
import { SDM } from "models/InitiateSessionResponse";
import { IError } from "utils/Api";
import ErrorModal from "components/shared/ErrorModal";

enum LoginStepsEnum {
  Login = 1,
  LoginOptions = 2,
  VerifyCode = 3,
}

const LoginContainer: React.FC = () => {
  const history = useHistory();
  const sessionId = getSessionMetadata()?.sessionId;
  const email = authService.getEmail();
  const phone = authService.getPhone()?.phone;
  const SDMs = authService.getSDMs();

  const [loginStep, setLoginStep] = React.useState<LoginStepsEnum>(
    LoginStepsEnum.Login
  );
  const [verifyCodeFeedback, setVerifyCodeFeedback] = React.useState<string>();
  const [errors, setErrors] = React.useState<IError[]>();
  const [selectedVerificationType, setSelectedVerificationType] =
    React.useState<LoginVerificationType>(LoginVerificationType.Email);
  const [isLoading, setIsLoading] = React.useState(false);
  const [codeResendTimer, setCodeResendTimer] =
    React.useState<NodeJS.Timeout>();
  const [
    secondsRemainingToAllowCodeResend,
    setSecondsRemainingToAllowCodeResend,
  ] = React.useState(0);
  const users = React.useRef<SDM[]>(
    (() => {
      let possibleUsers = Array<SDM>();
      if (email || (phone && !SDMs))
        possibleUsers.push({
          firstName: authService.getFirstName()!,
          lastName: authService.getLastName()!,
          email,
          needsAuthSetup: authService.getNeedsAuthSetup(),
          hasLogin: sessionStorage.getItem(StorageKeys.HasLogin) === "true",
          hasPhoneIdentity: !!authService.getPhone()?.isMfa,
          phone: authService.getPhone()?.phone,
          key: authService.getKey()!,
        });
      if (SDMs?.length) {
        possibleUsers = [...possibleUsers, ...SDMs];
      }
      return possibleUsers;
    })()
  );
  const [selectedUser, setSelectedUser] = React.useState<SDM | undefined>(
    () => {
      if (users.current.length === 1) {
        authService.setEmail(users.current[0].email);
        authService.setPhone({
          phone: users.current[0].phone,
          isMfa: !!users.current[0].hasPhoneIdentity,
        });
        authService.setKey(users.current[0].key);
        return users.current[0];
      }
      return;
    }
  );

  const maskedEmail = maskEmail(email);
  const maskedPhone = authService.getPhone()?.phone;

  const redirectTo =
    sessionId && !email && !phone && !SDMs
      ? `/session/${sessionId}`
      : undefined;

  const isShowLoginOptions = !!authService.getPhone()?.isMfa;

  React.useEffect(() => {
    sessionStorage.setItem(StorageKeys.RedirectTo, `/session/${sessionId}/sdm`);
    return () => {
      if (codeResendTimer) {
        clearInterval(codeResendTimer);
      }
    };
  }, [codeResendTimer, sessionId]);

  React.useEffect(() => {
    if (secondsRemainingToAllowCodeResend <= 0 && codeResendTimer) {
      clearInterval(codeResendTimer);
    }
  }, [codeResendTimer, secondsRemainingToAllowCodeResend]);

  const goBack = () => {
    setVerifyCodeFeedback(undefined);
    if (codeResendTimer) {
      clearInterval(codeResendTimer);
      setSecondsRemainingToAllowCodeResend(0);
    }
    if (loginStep === LoginStepsEnum.VerifyCode) {
      isShowLoginOptions
        ? setLoginStep(LoginStepsEnum.LoginOptions)
        : setLoginStep(LoginStepsEnum.Login);
    } else if (loginStep === LoginStepsEnum.LoginOptions) {
      setLoginStep(LoginStepsEnum.Login);
    } else {
      history.push(`/session/${sessionId}`);
    }
  };

  const onChangeUser = (user: SDM) => {
    setSelectedUser(user);
    setSelectedVerificationType(LoginVerificationType.Email);
    authService.setEmail(user?.email);
    authService.setKey(user?.key);
    sessionStorage.setItem(
      StorageKeys.HasLogin,
      (!!user.hasLogin).valueOf().toString()
    );
    if (user.phone) {
      authService.setPhone({
        phone: user?.phone,
        isMfa: !!user?.hasPhoneIdentity,
      });
    } else {
      authService.setPhone();
    }
  };

  const verifyCode = async (code: string) => {
    setIsLoading(true);
    const sessionId = getSessionMetadata()?.sessionId;
    const response = await authService.passwordLessSignInVerifyCode({
      method: selectedVerificationType,
      sessionId,
      code,
      scope: LoginScopeEnum.Session,
    });
    setIsLoading(false);
    if (response?.ok) {
      authService.saveAuthMetadata(response.payload.signature);
      if (authService.getIsAuthenticated()) {
        const redirectTo = sessionStorage.getItem(StorageKeys.RedirectTo);
        if (redirectTo) {
          sessionStorage.removeItem(StorageKeys.RedirectTo);
          sessionStorage.removeItem(StorageKeys.HasLogin);
          sessionStorage.removeItem(StorageKeys.AuthId);
          sessionStorage.removeItem(StorageKeys.RetrievalId);
          history.push(redirectTo);
        }
      }
    } else {
      if (response?.errors) {
        const errorMessages = response?.errors.map((m) => m.message);
        if (
          errorMessages.includes(Auth0Errors.ExpiredCode) ||
          errorMessages.includes(Auth0Errors.InvalidCode)
        )
          setVerifyCodeFeedback(VerifyCodeFeedback.InvalidCode);
        else if (errorMessages.some((m) => m.includes(Auth0Errors.Blocked))) {
          setVerifyCodeFeedback(VerifyCodeFeedback.UserBlocked);
        } else {
          setVerifyCodeFeedback(VerifyCodeFeedback.UnknownError);
        }
      }
    }
  };

  const sendCode = async (ignoreLastCode: boolean = false) => {
    setIsLoading(true);
    if (
      (authService.getEmail() ||
        (authService.getPhone()?.isMfa && authService.getPhone()?.phone)) &&
      secondsRemainingToAllowCodeResend <= 0
    ) {
      await authService.passwordLessSignInStart({
        method: selectedVerificationType,
        sessionId: getSessionMetadata()?.sessionId,
        ignoreLastCode,
      });

      setSecondsRemainingToAllowCodeResend(60);
      const timer = setInterval(() => {
        setSecondsRemainingToAllowCodeResend((prevValue) => prevValue - 1);
      }, 1000);
      setCodeResendTimer(timer);
    }
    setIsLoading(false);
  };

  const setupAuth = async () => {
    setIsLoading(true);
    if (selectedUser?.needsAuthSetup ?? true) {
      const response = await authService.setupAuth();
      if (response?.errors) {
        setErrors(
          response?.errors.map((err) => ({
            ...err,
            message: err.message,
          }))
        );
        setIsLoading(false);
        return;
      }
    }
    setLoginStep(
      !!authService.getPhone()?.isMfa
        ? LoginStepsEnum.LoginOptions
        : LoginStepsEnum.VerifyCode
    );
    setIsLoading(false);
  };

  if (redirectTo) return <Redirect to={redirectTo} />;
  return (
    <>
      {!!errors?.length && (
        <ErrorModal
          title={"An error has occurred"}
          errors={errors}
          onClose={() => {
            setErrors(undefined);
          }}
          customMessage={
            !!errors.find(
              (err) =>
                err.code === ErrorCode.CreateUpdateDeleteError &&
                err.message === AuthServiceErrors.CreateUserError
            )
              ? "An error occurred while creating a user. Please reach out to our support team at help@marbleapi.com."
              : undefined
          }
        />
      )}
      {loginStep === LoginStepsEnum.Login && (
        <Login
          users={users.current}
          selectedUser={selectedUser}
          onChangeUser={onChangeUser}
          onLogin={setupAuth}
          onGoBack={goBack}
          isLoading={isLoading}
        />
      )}

      {loginStep === LoginStepsEnum.LoginOptions && (
        <LoginVerificationOptions
          email={maskedEmail ?? ""}
          phone={maskedPhone ?? ""}
          onVerificationTypeSelected={(type) => {
            setSelectedVerificationType(type);
            setLoginStep(LoginStepsEnum.VerifyCode);
          }}
          onGoBack={goBack}
        />
      )}
      {loginStep === LoginStepsEnum.VerifyCode && (
        <VerifyCode
          email={maskedEmail ?? ""}
          phone={maskedPhone ?? ""}
          onGoBack={goBack}
          onSendCode={sendCode}
          onVerifyCode={verifyCode}
          secondsRemainingToAllowCodeResend={secondsRemainingToAllowCodeResend}
          isVerifyingCode={isLoading}
          verifyCodeFeedback={verifyCodeFeedback}
          verificationType={selectedVerificationType}
        />
      )}
    </>
  );
};

export default LoginContainer;
